"use strict";
/**
 * The kiosk package release contract — what a gaming PC asks for when it wants
 * to know whether it is running the current build, and what the operator sends
 * when publishing one.
 *
 * ONE VERSION COVERS THE WHOLE KIOSK PACKAGE: `PortalApp.exe`, the WebView2
 * wrappers beside it, and the bundled device agent. They ship together, are
 * built together, and a machine running a mixed pair is a state nobody can
 * reason about — so there is one string and it names the set.
 *
 * NOT VENUE-SCOPED, deliberately. The package is a Windows binary; it holds no
 * venue's data and no venue's secrets. A device's identity comes from its
 * pairing code, never from the download. Scoping releases per venue would mean
 * a venue could be stranded on a build with a known fault because nobody
 * published it to their tenant.
 *
 * The version format is `YYYY.MM.DD.HHmm` (UTC), stamped by the build. It is
 * fixed-width so lexical order is chronological order, and it needs no human to
 * remember to bump it — a forgotten bump is how a fleet silently stops
 * updating.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PORTAL_RELOAD_OUTCOMES = exports.APP_UPDATE_PUSH_OUTCOMES = exports.publishAppReleaseSchema = exports.APP_RELEASE_TOKEN_HEADER = exports.MAX_APP_RELEASE_BYTES = exports.APP_RELEASE_UPLOAD_FIELD = exports.kioskChecksumSchema = exports.kioskVersionSchema = exports.KIOSK_VERSION_PATTERN = void 0;
exports.isAppUpdateAvailable = isAppUpdateAvailable;
const zod_1 = require("zod");
/** `2026.07.30.2145` — UTC build stamp. Fixed width, so string order is time order. */
exports.KIOSK_VERSION_PATTERN = /^\d{4}\.\d{2}\.\d{2}\.\d{4}$/;
exports.kioskVersionSchema = zod_1.z
    .string()
    .regex(exports.KIOSK_VERSION_PATTERN, 'expected a kiosk version like 2026.07.30.2145');
/** Lowercase hex SHA-256 of the published zip. */
exports.kioskChecksumSchema = zod_1.z
    .string()
    .regex(/^[a-f0-9]{64}$/, 'expected a lowercase hex sha256 digest');
/** Field name of the zip part on the publish request. */
exports.APP_RELEASE_UPLOAD_FIELD = 'package';
/**
 * Ceiling for a published package. The real one is ~1 MB: the update carries
 * the exe, three wrappers and the agent bundle, and deliberately NOT `node.exe`
 * — that arrives once on the USB stick and never changes. A payload anywhere
 * near this limit means something unintended got swept into the zip.
 */
exports.MAX_APP_RELEASE_BYTES = 64 * 1024 * 1024;
/** Header carrying the operator's publish token. */
exports.APP_RELEASE_TOKEN_HEADER = 'x-release-token';
exports.publishAppReleaseSchema = zod_1.z.object({
    version: exports.kioskVersionSchema,
    /** Shown to staff on the update prompt. Empty is allowed; a lie is not. */
    notes: zod_1.z.string().max(2000).default(''),
});
/**
 * True when `installed` should be offered `published`.
 *
 * INEQUALITY, not "newer than". The published release is the authority on what
 * the fleet should run, and the case that matters most is the one greater-than
 * gets wrong: an update ships, a PC turns out to be broken by it, and the
 * operator republishes the previous build to recover. Under a greater-than test
 * every affected machine would decide it was already fine and stay broken.
 *
 * A machine with no version at all (an install predating this feature) is
 * offered the update.
 */
function isAppUpdateAvailable(installed, published) {
    if (!published)
        return false;
    return installed !== published;
}
/**
 * What happened when staff pressed "update the PCs now", per machine.
 *
 * - `sent` — that PC was told to fetch and install straight away.
 * - `staged` — somebody is playing on it, so it was told to DOWNLOAD only. It
 *   installs at its next logon. Interrupting a paying customer for a release
 *   is the one thing this feature must never do.
 * - `offline` — its agent is not connected. It will pick the release up on its
 *   own half-hourly check once it is back.
 * - `unsupported` — an older kiosk build with no handler for the nudge. It
 *   still updates on its own schedule; it just cannot be hurried.
 * - `no_agent` — nothing was ever paired here. A pool table has no app.
 */
exports.APP_UPDATE_PUSH_OUTCOMES = [
    'sent',
    'staged',
    'offline',
    'unsupported',
    'no_agent',
];
/* -------------------------------------------------------------------------- */
/* Refreshing the portal SCREEN, which is a different thing entirely           */
/* -------------------------------------------------------------------------- */
/**
 * What one PC did when asked to reload its portal page.
 *
 * THE DISTINCTION THIS TYPE EXISTS TO MAKE. Updating swaps the Windows
 * application; refreshing re-fetches the web page it displays. They are pressed
 * for different reasons and fail in different ways, and sharing one outcome
 * list would have hidden the one that matters here — `skipped_in_session`.
 *
 * - `sent` — the machine was told, and reloaded.
 * - `skipped_in_session` — somebody is playing on it. A reload drops them to
 *   the sign-in screen mid-sitting, so it is refused, and the PC keeps the page
 *   it has until the sitting ends. NOT a failure: it is the rule working.
 * - `offline` — its agent is not connected. It gets the current page whenever
 *   it next starts, which is what starting does.
 * - `unsupported` — a kiosk build from before this existed. Sign Windows out
 *   and back in on that machine, or update it.
 * - `no_agent` — nothing paired here. A pool table has no screen to refresh.
 */
exports.PORTAL_RELOAD_OUTCOMES = [
    'sent',
    'skipped_in_session',
    'offline',
    'unsupported',
    'no_agent',
];
//# sourceMappingURL=app-releases.js.map